/*
  Oasis overlay coordinator.

  Watches the page for any open dialog (reservation confirmation, quiz deposit
  contribution popup, Calendly scheduler, product-page modals, member club
  popup, blueprint popups, Dawn modal-dialog / cart drawer, native <dialog>)
  and mirrors that state on <body class="oasis-modal-open">.

  CSS elsewhere uses that class to hide the floating chat launcher
  (snippets/oasis-chatbot.liquid) and the contact pill (assets/oasis-cta.css)
  so they never sit on top of purchase copy or close controls on narrow
  screens. The chat snippet also listens for the "oasis:modal-state" event to
  defer its proactive greeting while a dialog is open.

  Detection is generic: a candidate is "open" only when it is actually
  rendered (not hidden, not aria-hidden, has a box). Selectors are listed in
  MODAL_SELECTORS; add new dialogs there.
*/
(function () {
  'use strict';

  if (window.OasisOverlayCoordinator) return;

  var MODAL_SELECTORS = [
    'dialog[open]',
    '.calendly-overlay',
    '.oasis-confirm-overlay',
    '.quiz-contribution-modal.active',
    '.discovery-popup',
    '.exit-popup.active',
    '.oasis-popup-overlay.active',
    '[id^="oasisPopupOverlay-"].active',
    '.modal-overlay',
    '.product-drawer.open',
    'cart-drawer.active',
    'modal-dialog[open]',
    'details-modal details[open]',
    '[role="dialog"][aria-modal="true"]'
  ];

  /* Non-modal dialogs that must never hide the chat (consent banner uses aria-modal="false"). */
  var IGNORE_SELECTORS = [
    '#OasisConsentBanner',
    'oasis-chat-widget'
  ];

  var docEl = document.documentElement;
  var currentState = null;
  var frame = null;

  function isRendered(el) {
    if (!el || !el.isConnected) return false;
    if (el.hidden || el.hasAttribute('inert')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    /* Faded-out and inert (opacity 0 + pointer-events none) counts as closed; a plain
       opacity transition in progress does not, and transitionend re-evaluates anyway. */
    if (parseFloat(style.opacity) === 0 && style.pointerEvents === 'none') return false;
    var rect = el.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return false;
    /* Slide-over panels parked fully off-screen still have a box; treat them as closed. */
    var viewportWidth = window.innerWidth || docEl.clientWidth || 0;
    var viewportHeight = window.innerHeight || docEl.clientHeight || 0;
    if (rect.right <= 0 || rect.bottom <= 0 || rect.left >= viewportWidth || rect.top >= viewportHeight) return false;
    return true;
  }

  function isIgnored(el) {
    for (var i = 0; i < IGNORE_SELECTORS.length; i += 1) {
      try {
        if (el.matches(IGNORE_SELECTORS[i]) || el.closest(IGNORE_SELECTORS[i])) return true;
      } catch (error) { /* unsupported selector, ignore */ }
    }
    return false;
  }

  function findOpenModal() {
    for (var i = 0; i < MODAL_SELECTORS.length; i += 1) {
      var nodes;
      try {
        nodes = document.querySelectorAll(MODAL_SELECTORS[i]);
      } catch (error) {
        continue;
      }
      for (var j = 0; j < nodes.length; j += 1) {
        if (!isIgnored(nodes[j]) && isRendered(nodes[j])) return nodes[j];
      }
    }
    /* Class hooks set by scripts that do not expose the dialog itself. */
    if (docEl.classList.contains('oasis-confirm-open')) return docEl;
    if (docEl.classList.contains('oasis-calendly-open')) return docEl;
    if (document.body && document.body.classList.contains('drawer-open')) return document.body;
    return null;
  }

  function setState(open, source) {
    if (currentState === open) return;
    currentState = open;
    if (document.body) document.body.classList.toggle('oasis-modal-open', open);
    try {
      document.dispatchEvent(new CustomEvent('oasis:modal-state', {
        detail: { open: open, source: source || null }
      }));
    } catch (error) { /* CustomEvent unavailable, class toggle still applied */ }
  }

  function evaluate() {
    frame = null;
    if (!document.body) return;
    var openModal = findOpenModal();
    setState(Boolean(openModal), openModal);
  }

  function schedule() {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(evaluate);
  }

  function start() {
    var observer = new MutationObserver(schedule);
    observer.observe(docEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden']
    });
    document.addEventListener('transitionend', schedule, true);
    window.addEventListener('resize', schedule);
    window.addEventListener('pageshow', schedule);
    evaluate();
  }

  window.OasisOverlayCoordinator = {
    isModalOpen: function () { return currentState === true; },
    refresh: schedule,
    selectors: MODAL_SELECTORS
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
